import { Icon20Cancel, Icon20InfoCircleOutline } from '@vkontakte/icons';
import clsx from 'clsx';
import React, { type ReactNode, memo, useEffect } from 'react';
import { Button, ButtonSizeMode } from 'reactApp/ui/Button/Button';

import styles from './InformationAlertBanner.css';

export const enum InformationBannerType {
    alert = 'alert',
    info = 'info',
}

interface InformationAlertProps {
    title: string | ReactNode;
    content: string | ReactNode;
    primaryButtonTitle?: string;
    secondaryButtonTitle?: string | ReactNode;
    closable?: boolean;
    type: InformationBannerType;
    hideIcon?: boolean;
    customPaddingLeft?: string;
    buttonsMaxWidth?: string;
    primaryButtonClassName?: string;
    secondaryButtonClassName?: string;
    responsiveButtons?: boolean;

    onShow?(): void;
    onCancel?(): void;

    onPrimaryClick?(): void;

    onSecondaryClick?(): void;
}

export const InformationAlertBanner = memo<InformationAlertProps>(
    ({
        title,
        content,
        primaryButtonTitle,
        secondaryButtonTitle,
        type,
        closable,
        hideIcon,
        customPaddingLeft,
        buttonsMaxWidth,
        primaryButtonClassName,
        secondaryButtonClassName,
        responsiveButtons,
        onCancel,
        onShow,
        onPrimaryClick,
        onSecondaryClick,
    }) => {
        useEffect(() => {
            onShow?.();
        }, [onShow]);

        const rootStyle = customPaddingLeft ? { paddingLeft: customPaddingLeft } : undefined;
        const buttonsStyle = buttonsMaxWidth ? { maxWidth: buttonsMaxWidth } : undefined;

        return (
            <div
                className={clsx(styles.root, styles[`root_${type}`], { [styles.root_responsive]: responsiveButtons })}
                data-qa-id="information-alert-banner"
                style={rootStyle}
            >
                {!hideIcon && <Icon20InfoCircleOutline className={styles.icon} />}
                <div className={clsx(styles.content, { [styles.content_noIcon]: hideIcon })}>
                    <div className={styles.title} data-qa-id="title">
                        {title}
                    </div>
                    <div className={styles.text} data-qa-id="content">
                        {content}
                    </div>
                </div>
                <div className={clsx(styles.buttons, { [styles.buttons_responsive]: responsiveButtons })} style={buttonsStyle}>
                    {primaryButtonTitle && (
                        <Button
                            className={clsx(styles.button, primaryButtonClassName)}
                            data-name="primary-bttn"
                            data-qa-id="primary-bttn"
                            theme="octavius"
                            sizeMode={ButtonSizeMode.small}
                            onClick={onPrimaryClick}
                            primary={type === InformationBannerType.info}
                            error={type === InformationBannerType.alert}
                        >
                            {primaryButtonTitle}
                        </Button>
                    )}
                    {secondaryButtonTitle && (
                        <Button
                            className={clsx(styles.button, secondaryButtonClassName)}
                            onClick={onSecondaryClick}
                            theme="octavius"
                            sizeMode={ButtonSizeMode.small}
                            data-name="secondary-bttn"
                            data-qa-id="secondary-bttn"
                        >
                            {secondaryButtonTitle}
                        </Button>
                    )}
                    {closable && <Icon20Cancel className={styles.cross} onClick={onCancel} />}
                </div>
            </div>
        );
    }
);

InformationAlertBanner.displayName = 'InformationAlertBanner';
